<template>
  <div>
    <h3 class="display-4" @click="enableDebug($event)">Manage Supervisors</h3>

    <o-list-toolbar
      :filters-list="filter_list"
      :search-fields-list="search_field_list"
      :selected-filter="selected_filter"
      :list-options="list_options"
      @reset_table="reset_table"
      @options_changed="update_table($event)"
      @filter_changed="update_filter($event)"
      :schema="schema"
      :query-select-options="options"
      :new-to="{ name: 'supervisor.create', params: { id: 0 } }"
      new-button-text="New Supervisor"
    >
      <template slot="middle">
        <o-list-multi-actions
          :schema="schema"
          :selection="selected"
          @delete="doDeleteItems"
          @merge="doMergeItems"
          enable-delete
          enable-merge
        />
      </template>
    </o-list-toolbar>

    <b-table
      striped
      bordered
      :fields="fields"
      :items="items"
      head-variant="light"
      class="o-list"
      thead-class="o-gradient-header"
      @row-selected="onRowSelected"
      :selectable="selected.selectable"
      :key="selected.selectable"
      :busy="working"
      no-local-sorting
      @sort-changed="change_sort"
      :sort-by="list_options.sort"
      :sort-desc="list_options.order === 'desc'"
    >
      <template v-slot:cell(_selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <span aria-hidden="true">&check;</span>
          <span class="sr-only">Selected</span>
        </template>
        <template v-else>
          <span aria-hidden="true">&nbsp;</span>
          <span class="sr-only">Not selected</span>
        </template>
      </template>
      <template
        slot="top-row"
        slot-scope="{ columns, fields }"
        v-if="filterRow"
      >
        <td :key="field.key" class="p-0" v-for="field in fields">
          <span v-if="field.key === 'active_end_date'">
            <o-flatpickr-wrapper
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.active_end_date"
            ></o-flatpickr-wrapper>
          </span>
          <span v-if="field.key === 'active_start_date'">
            <o-flatpickr-wrapper
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.active_start_date"
            ></o-flatpickr-wrapper>
          </span>
          <span v-if="field.key === 'created_date'">
            <o-flatpickr-wrapper
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.created_date"
            ></o-flatpickr-wrapper>
          </span>
          <span v-if="field.key === 'crew_leader'">
            <o-boolean-select
              name="crew_leader"
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.crew_leader"
              nolabel
            ></o-boolean-select>
          </span>
          <span v-if="field.key === 'customer'">
            <o-form-select
              v-model="list_options.qbe.customer"
              :options="optionCustomerItems"
              text-field="name"
              value-field="id"
              sort-field="name"
              blank-option-text=""
              :loading="optionCustomerWorking"
              @change="filter_changed"
              name="customer"
            >
              <template v-slot:first>
                <option :value="null"></option>
              </template>
            </o-form-select>
          </span>
          <span v-if="field.key === 'email'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.email"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'mobile_phone_no'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.mobile_phone_no"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'modified_date'">
            <o-flatpickr-wrapper
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.modified_date"
            ></o-flatpickr-wrapper>
          </span>
          <span v-if="field.key === 'name'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.name"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'original_data'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.original_data"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'replaced_data'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.replaced_data"
            ></b-form-input>
          </span>
        </td>
      </template>
      <template slot="cell(active_end_date)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'supervisor.edit', params: { id: item.id } }"
          :when="first_column_key === 'active_end_date'"
        >
          <date-display
            :value="item.active_end_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template slot="cell(active_start_date)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'supervisor.edit', params: { id: item.id } }"
          :when="first_column_key === 'active_start_date'"
        >
          <date-display
            :value="item.active_start_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template slot="cell(created_date)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'supervisor.edit', params: { id: item.id } }"
          :when="first_column_key === 'created_date'"
        >
          <date-display
            :value="item.created_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template slot="cell(crew_leader)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'supervisor.edit', params: { id: item.id } }"
          :when="first_column_key === 'crew_leader'"
        >
          <boolean-display :value="item.crew_leader"></boolean-display>
        </o-link-cell>
      </template>
      <template slot="cell(customer)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'supervisor.edit', params: { id: item.id } }"
          :when="first_column_key === 'customer'"
        >
          <lookup-display
            :list="optionCustomerItemsById"
            :value="item.customer"
            display-field="name"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>
      <template slot="cell(email)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'supervisor.edit', params: { id: item.id } }"
          :when="first_column_key === 'email'"
        >
          <default-display :value="item.email"></default-display>
        </o-link-cell>
      </template>
      <template slot="cell(mobile_phone_no)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'supervisor.edit', params: { id: item.id } }"
          :when="first_column_key === 'mobile_phone_no'"
        >
          <default-display :value="item.mobile_phone_no"></default-display>
        </o-link-cell>
      </template>
      <template slot="cell(modified_date)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'supervisor.edit', params: { id: item.id } }"
          :when="first_column_key === 'modified_date'"
        >
          <date-display
            :value="item.modified_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template slot="cell(name)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'supervisor.edit', params: { id: item.id } }"
          :when="first_column_key === 'name'"
        >
          <default-display :value="item.name"></default-display>
        </o-link-cell>
      </template>
      <template slot="cell(original_data)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'supervisor.edit', params: { id: item.id } }"
          :when="first_column_key === 'original_data'"
        >
          {{ item.original_data }}
        </o-link-cell>
      </template>
      <template slot="cell(replaced_data)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'supervisor.edit', params: { id: item.id } }"
          :when="first_column_key === 'replaced_data'"
        >
          {{ item.replaced_data }}
        </o-link-cell>
      </template>

      <template slot="empty">
        <p>
          <emoticon-sad-icon large></emoticon-sad-icon>
        </p>
        No Data Found. Please
        <router-link :to="{ name: 'supervisor.create' }"
          >create a new item</router-link
        >
      </template>
    </b-table>

    <o-list-paging
      :limit="list_options.limit"
      :page-options="rows_per_page_items"
      :offset="list_options.offset"
      :total-items="total_items"
      @change-limit="change_limit"
      @change-page="change_page"
    />
  </div>
</template>

<style></style>

<script>
import {
  CreateCodeTableFetchMixin,
  CreateGroupPermissionMixin,
  CreateListPageMixin,
  MultiActionsMixin,
} from "../../../vue-ocom/mixins";
import {
  fieldToTableColumn,
  GetSearchFields,
} from "../../../vue-ocom/schema/schema_utils";
import { cleanUpModel } from "../../../vue-ocom/schema/schema_vuetify";
import schema from "../../schema/index";

import OListToolbar from "../../../vue-ocom/components/list/OListToolbar";
import OListPaging from "../../../vue-ocom/components/list/OListPaging";
import DefaultDisplay from "../../../vue-ocom/components/display/DefaultDisplay";
import DateDisplay from "../../../vue-ocom/components/display/DateDisplay";
import NumberDisplay from "../../../vue-ocom/components/display/NumberDisplay";
import BooleanDisplay from "../../../vue-ocom/components/display/BooleanDisplay";
import LookupDisplay from "../../../vue-ocom/components/display/LookupDisplay";
import EmoticonSadIcon from "vue-material-design-icons/EmoticonSad";
import EmailIcon from "vue-material-design-icons/Email";
import LinkIcon from "vue-material-design-icons/Link";

export default {
  components: {
    EmoticonSadIcon,
    EmailIcon,
    LinkIcon,
    OListPaging,
    OListToolbar,
    DateDisplay,
    DefaultDisplay,
    BooleanDisplay,
    NumberDisplay,
    LookupDisplay,
  },
  mixins: [
    CreateListPageMixin("Supervisor", "supervisor"),
    MultiActionsMixin,
    CreateGroupPermissionMixin("Supervisor"),
    CreateCodeTableFetchMixin(["Customer"]),
  ],
  data: function () {
    return {
      schemaModel: cleanUpModel(
        schema.Supervisor,
        {}
      ) /*Use this.schema instead */,

      search_field_list: GetSearchFields(schema.Supervisor),

      filterRow: true /* Set to False to disable filter row */,
      filter_list: [
        { name: "Active", code: "active", options: { offset: 0, sort: "id" } },
        {
          name: "Non Active",
          code: "inactive",
          options: { offset: 0, sort: "id" },
        },
        { name: "All", code: "all", options: { offset: 0, sort: "id" } },
      ],

      user_column_selection: null /* If user can customise the columns set them here */,
      column_override_params: {
        /* Put any column overrides here key by fieldname */
      },
    };
  },
  computed: {
    options() {
      return {
        Customer: {
          options: this.optionCustomerItems,
          textField: "name",
          valueField: "id",
        },
      };
    },
    fields() {
      const vm = this;
      const selectedColumns = this.user_column_selection || [
        "name",
        "customer",
        "crew_leader",
        "mobile_phone_no",
        "email",
        "active_start_date",
        "active_end_date",
      ];
      const fields = selectedColumns.map(function (item) {
        return fieldToTableColumn(
          vm.schema,
          item,
          vm.column_override_params[item]
        );
      });

      return this.addSelectionColums(
        this.filterTableColumns(this.schema, fields)
      );
    },
    first_column_key() {
      if (this.fields) {
        return this.fields[0].key;
      } else {
        return null;
      }
    },
  },
};
</script>
